import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteImage } from '@/contexts/SiteImagesContext';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Menu, Phone, Clock, User, LogOut, Calendar, ChevronDown, Stethoscope } from 'lucide-react';
import { useState } from 'react';

const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'Chi Siamo', href: '/chi-siamo', sub: [
    { label: 'Chi Siamo', href: '/chi-siamo' },
    { label: 'Operazione Cultura', href: '/operazione-cultura' },
  ]},
  { label: 'Servizi', href: '/servizi' },
  { label: 'Equipe', href: '/equipe' },
  { label: 'Eventi', href: '/eventi' },
  { label: 'News', href: '/news' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contatti', href: '/contatti' },
];

export default function PublicLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const logoHeader = useSiteImage('logo_header');
  const logoFooter = useSiteImage('logo_footer');
  const siteSettings = useSiteSettings();
  const bookingActive = siteSettings.prenotazioni_attive !== false || (user && (user.role === 'admin' || user.role === 'operator'));
  const bookingLink = bookingActive ? '/prenota' : '/contatti';
  const bookingLabel = bookingActive ? 'Prenota' : 'Contattaci';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top bar */}
      <div className="bg-[#1F2A37] text-white text-xs py-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Phone size={12} /> 02.6693281 - 02.67075732</span>
            <span className="hidden sm:flex items-center gap-1"><Clock size={12} /> Lun-Ven 9-19 | Sab 9-13</span>
          </div>
          <span className="hidden sm:block">Via Settembrini 7, Milano</span>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            {logoHeader && <img src={logoHeader} alt="Studio Medico Boscovich" className="h-7 hidden sm:block" />}
            {logoHeader && <img src={logoHeader} alt="SMB" className="h-6 sm:hidden" />}
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map(item => 
              item.sub ? (
                <DropdownMenu key={item.href}>
                  <DropdownMenuTrigger asChild>
                    <button className={`px-3 py-2 text-sm rounded-md transition-colors duration-200 flex items-center gap-1 ${
                      location.pathname === item.href || location.pathname === '/operazione-cultura'
                        ? 'text-[#238A92] bg-[hsl(var(--secondary))]'
                        : 'text-[#1F2A37] hover:text-[#238A92] hover:bg-[hsl(var(--secondary))]'
                    }`}>
                      {item.label} <ChevronDown size={12} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {item.sub.map(sub => (
                      <DropdownMenuItem key={sub.href} asChild>
                        <Link to={sub.href} className={location.pathname === sub.href ? 'text-[#238A92] font-medium' : ''}>{sub.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link key={item.href} to={item.href} className={`px-3 py-2 text-sm rounded-md transition-colors duration-200 ${location.pathname === item.href ? 'text-[#238A92] bg-[hsl(var(--secondary))]' : 'text-[#1F2A37] hover:text-[#238A92] hover:bg-[hsl(var(--secondary))]'}`}>{item.label}</Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1" data-testid="user-menu-trigger">
                    <User size={16} /><span className="hidden sm:inline">{user.nome}</span><ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(user.role === 'admin' || user.role === 'operator') && <DropdownMenuItem asChild><Link to="/backoffice" data-testid="menu-backoffice-link">Backoffice</Link></DropdownMenuItem>}
                  {user.role === 'doctor' && <DropdownMenuItem asChild><Link to="/area-dottore" data-testid="menu-doctor-area-link"><Stethoscope size={14} className="mr-2" /> Area Dottore</Link></DropdownMenuItem>}
                  {user.role === 'patient' && <DropdownMenuItem asChild><Link to="/i-miei-appuntamenti"><Calendar size={14} className="mr-2" /> I Miei Appuntamenti</Link></DropdownMenuItem>}
                  <DropdownMenuItem asChild><Link to="/profilo"><User size={14} className="mr-2" /> Profilo</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}><LogOut size={14} className="mr-2" /> Esci</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="login-button">Accedi</Button></Link>
            )}
            <Link to={bookingLink}>
              <Button size="sm" className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="site-header-book-appointment-button">
                <Calendar size={14} className="mr-1" /> {bookingLabel}
              </Button>
            </Link>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden"><Menu size={20} /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="flex flex-col gap-1 mt-8">
                  {NAV_ITEMS.flatMap(item => 
                    item.sub
                      ? item.sub.map(sub => (
                          <Link key={sub.href} to={sub.href} onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm rounded-md transition-colors duration-200 ${location.pathname === sub.href ? 'text-[#238A92] bg-[hsl(var(--secondary))] font-medium' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'}`}>
                            {sub.label}
                          </Link>
                        ))
                      : [(
                          <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm rounded-md transition-colors duration-200 ${location.pathname === item.href ? 'text-[#238A92] bg-[hsl(var(--secondary))] font-medium' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'}`}>
                            {item.label}
                          </Link>
                        )]
                  )}
                  <Link to={bookingLink} onClick={() => setMobileOpen(false)} className="mt-4">
                    <Button className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white">{bookingActive ? 'Prenota Appuntamento' : 'Contattaci'}</Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      {/* Footer */}
      <footer className="bg-[#1F2A37] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {logoFooter && <img src={logoFooter} alt="Studio Medico Boscovich" className="h-7 opacity-70" />}
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">Siamo uno Studio Medico Associato dove lavorano diversi specialisti, offrendo una gamma di servizi sempre più vasta.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4" style={{fontFamily:'Space Grotesk'}}>Contatti</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Via Settembrini 7, Milano</p>
                <p>02.6693281 - 02.67075732</p>
                <p>info@studiomedicoboscovich.com</p>
                <p>Lun-Ven: 9:00-19:00</p>
                <p>Sabato: 9:00-13:00</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4" style={{fontFamily:'Space Grotesk'}}>Link Rapidi</h4>
              <div className="space-y-2 text-sm">
                <Link to="/servizi" className="block text-gray-400 hover:text-[#238A92] transition-colors duration-200">I Nostri Servizi</Link>
                <Link to="/equipe" className="block text-gray-400 hover:text-[#238A92] transition-colors duration-200">Equipe Medica</Link>
                <Link to="/prenota" className="block text-gray-400 hover:text-[#238A92] transition-colors duration-200">Prenota Appuntamento</Link>
                <Link to="/faq" className="block text-gray-400 hover:text-[#238A92] transition-colors duration-200">FAQ</Link>
                <Link to="/contatti" className="block text-gray-400 hover:text-[#238A92] transition-colors duration-200">Contatti</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-xs text-gray-500">
            <p>Copyright 2025 - Studio Medico Boscovich - P.IVA 11080350157</p>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              <Link to="/privacy-policy" className="hover:text-[#238A92] transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/cookie-policy" className="hover:text-[#238A92] transition-colors">Cookie Policy</Link>
              <span>|</span>
              <button onClick={() => { try { localStorage.removeItem('smb_cookie_consent'); } catch (e) { /* cookie clear */ } window.location.reload(); }} className="hover:text-[#238A92] transition-colors">Gestisci Cookie</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
